import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { HeadingBlock, newHeadingBlock } from '../Blocks/Heading/HeadingBlock';
import {
  newSubheadingBlock,
  SubheadingBlock,
} from '../Blocks/Subheading/SubheadingBlock';
import { TableBlock, newTableBlock } from '../Blocks/Table/TableBlock';
import BuildEditorInstructions from './BuildEditorInstructions';
import { newBlankDomainBlock, newBlankTextFieldBlock } from './newBlankBlock';
import { SuggestionsMessage } from './SuggestionsMessage';
import { BlocksContainer } from './Blocks/BlocksContainer';
import QualityAssessmentAddMenu from './QualityAssessment/AddMenu';
import DataExtractionAddMenu from './DataExtraction/AddMenu';
import {
  TemplateViewModel,
  hydrateBlock,
} from 'concepts/Extraction/ViewModel/TemplateViewModel';
import { TemplateBlock, BlockID } from 'concepts/Extraction/Blocks/BaseBlock';
import {
  newTextFieldBlock,
  TextFieldBlock,
} from 'concepts/Extraction/Blocks/TextField/TextFieldBlock';
import {
  useFocusManager,
  FocusManagerProvider,
  FocusOptions,
} from 'contexts/FocusManager/FocusManager';
import {
  newMultipleChoiceBlock,
  MultipleChoiceBlock,
} from 'concepts/Extraction/Blocks/MultipleChoice/MultipleChoiceBlock';
import {
  newCheckboxesBlock,
  CheckboxesBlock,
} from 'concepts/Extraction/Blocks/Checkboxes/CheckboxesBlock';
import {
  newDomainBlock,
  DomainBlock,
} from 'concepts/Extraction/Blocks/Domain/DomainBlock';
import { ExtractionFormType } from 'concepts/Extraction/ExtractionFormType';
import {
  BlockAction,
  applyBlockAction,
} from 'concepts/Extraction/Blocks/BlockAction';

type ChangeHandler = (prevViewModel: TemplateViewModel) => TemplateViewModel;

export type BuildEditorProps = {
  formType: ExtractionFormType;
  viewModel: TemplateViewModel;
  selectedBlockID: BlockID | null;
  onChange: (changer: ChangeHandler) => void;
  onSelectBlock: (blockID: BlockID) => void;
  generateNewBlock?: () => TemplateBlock;
  showRevertButton: boolean;
  revertButtonOnClick: () => void;
  showReuseTemplateLink?: boolean;
  onReuseTemplate?: () => void;
  showSuggestionsMessage?: boolean;
  isFieldSuggestable?: (label: string) => boolean;
};

function BuildQualityAssessmentEditor({
  viewModel,
  selectedBlockID,
  onChange,
  onSelectBlock,
  revertButtonOnClick,
  showRevertButton,
  showReuseTemplateLink,
  onReuseTemplate,
}: BuildEditorProps): JSX.Element {
  const requestFocus = useFocusManager();
  const requestFocusOnBlock = useCallback(
    (blockID: string, focusOption: FocusOptions) => {
      requestFocus(
        `[data-block-id="${blockID}"] textarea, [data-block-id="${blockID}"] input`,
        focusOption
      );
    },
    [requestFocus]
  );

  // Read via a ref rather than closing over `viewModel` directly, so
  // `dispatch`'s identity doesn't change on every keystroke (it would
  // otherwise change every time viewModel does, defeating BlockRow's memo).
  const viewModelRef = useRef(viewModel);
  useLayoutEffect(() => {
    viewModelRef.current = viewModel;
  }, [viewModel]);

  const dispatch = useCallback(
    (action: BlockAction<DomainBlock>) => {
      if (action.type === 'selectBlock') {
        onSelectBlock(action.blockID);
        return;
      }

      const { newViewModel, focusOnBlockID } = applyBlockAction(
        viewModelRef.current,
        action,
        newBlankDomainBlock
      );
      onChange(() => newViewModel);
      if (focusOnBlockID) {
        requestFocusOnBlock(focusOnBlockID, {
          when:
            action.type === 'addAfter' ? 'nextAnimationFrame' : 'nextRender',
        });
      }
    },
    [onSelectBlock, onChange, requestFocusOnBlock]
  );

  const renderMenu = useCallback(
    (block: DomainBlock) => (
      <QualityAssessmentAddMenu
        onAdd={() => {
          dispatch({
            type: 'addAfter',
            afterBlockID: block.id,
            newBlock: newDomainBlock(''),
          });
        }}
        onDuplicate={() => {
          // Create a new block, copying ONLY the judgements
          const newBlock = {
            ...newDomainBlock(''),
            judgements: block.judgements,
          };
          dispatch({ type: 'addAfter', afterBlockID: block.id, newBlock });
        }}
      />
    ),
    [dispatch]
  );

  return (
    <div>
      <BuildEditorInstructions
        onReset={() => {
          dispatch({
            type: 'replaceAll',
            newBlocks: [newBlankDomainBlock()],
          });
        }}
        showRevertButton={showRevertButton}
        revertButtonOnClick={revertButtonOnClick}
        showReuseTemplateLink={showReuseTemplateLink}
        onReuseTemplate={onReuseTemplate}
      />
      <BlocksContainer
        blocks={viewModel.blocks as any}
        dispatch={dispatch}
        selectedBlockID={selectedBlockID}
        renderMenu={renderMenu}
      />
    </div>
  );
}

const BuildDataExtractionEditor = ({
  viewModel,
  selectedBlockID,
  onChange,
  onSelectBlock,
  revertButtonOnClick,
  showRevertButton,
  showReuseTemplateLink,
  onReuseTemplate,
  showSuggestionsMessage = false,
  isFieldSuggestable,
}: BuildEditorProps): JSX.Element => {
  const requestFocus = useFocusManager();
  const requestFocusOnBlock = useCallback(
    (blockID: string, focusOption: FocusOptions) => {
      requestFocus(
        `[data-block-id="${blockID}"] textarea, [data-block-id="${blockID}"] input`,
        focusOption
      );
    },
    [requestFocus]
  );

  // Read via a ref rather than closing over `viewModel` directly, so
  // `dispatch`'s identity doesn't change on every keystroke (it would
  // otherwise change every time viewModel does, defeating BlockRow's memo).
  const viewModelRef = useRef(viewModel);
  useLayoutEffect(() => {
    viewModelRef.current = viewModel;
  }, [viewModel]);

  const dispatch = useCallback(
    (
      action: BlockAction<
        | TextFieldBlock
        | MultipleChoiceBlock
        | CheckboxesBlock
        | HeadingBlock
        | SubheadingBlock
        | TableBlock
      >
    ) => {
      if (action.type === 'selectBlock') {
        onSelectBlock(action.blockID);
        return;
      }

      const { newViewModel, focusOnBlockID } = applyBlockAction(
        viewModelRef.current,
        action,
        newBlankTextFieldBlock
      );
      onChange(() => newViewModel);
      if (focusOnBlockID) {
        requestFocusOnBlock(focusOnBlockID, {
          when:
            action.type === 'addAfter' ? 'nextAnimationFrame' : 'nextRender',
        });
      }
    },
    [onSelectBlock, onChange, requestFocusOnBlock]
  );

  const renderMenu = useCallback(
    (
      block:
        | TextFieldBlock
        | MultipleChoiceBlock
        | CheckboxesBlock
        | HeadingBlock
        | SubheadingBlock
        | TableBlock
    ) => (
      <DataExtractionAddMenu
        onAdd={(blockType) => {
          let newBlock:
            | TextFieldBlock
            | MultipleChoiceBlock
            | CheckboxesBlock
            | HeadingBlock
            | SubheadingBlock
            | TableBlock;
          if (blockType === 'MultipleChoice') {
            newBlock = newMultipleChoiceBlock('');
          } else if (blockType === 'Checkboxes') {
            newBlock = newCheckboxesBlock('');
          } else if (blockType === 'Heading') {
            newBlock = newHeadingBlock('');
          } else if (blockType === 'Subheading') {
            newBlock = newSubheadingBlock('');
          } else if (blockType === 'Table') {
            newBlock = newTableBlock('');
          } else {
            newBlock = newTextFieldBlock('');
          }

          // Hydrate the block with display-only fields (icon, title, etc.)
          const hydratedBlock = hydrateBlock(newBlock);

          dispatch({
            type: 'addAfter',
            afterBlockID: block.id,
            newBlock: hydratedBlock as typeof newBlock,
          });
        }}
      />
    ),
    [dispatch]
  );

  return (
    <div>
      <BuildEditorInstructions
        onReset={() => {
          dispatch({
            type: 'replaceAll',
            newBlocks: [newBlankTextFieldBlock()],
          });
        }}
        showRevertButton={showRevertButton}
        revertButtonOnClick={revertButtonOnClick}
        showReuseTemplateLink={showReuseTemplateLink}
        onReuseTemplate={onReuseTemplate}
      />
      {showSuggestionsMessage && <SuggestionsMessage />}
      <BlocksContainer
        blocks={viewModel.blocks as any}
        dispatch={dispatch}
        selectedBlockID={selectedBlockID}
        isFieldSuggestable={isFieldSuggestable}
        renderMenu={renderMenu}
      />
    </div>
  );
};

function BuilderFactory(props: BuildEditorProps): JSX.Element {
  switch (props.formType) {
    case 'quality_assessment':
      return <BuildQualityAssessmentEditor {...props} />;
    default:
      return <BuildDataExtractionEditor {...props} />;
  }
}

export default function BuildEditor(props: BuildEditorProps): JSX.Element {
  return (
    <FocusManagerProvider>
      <BuilderFactory {...props} />
    </FocusManagerProvider>
  );
}
